/**
 * MeetingDetailModal — the per-meeting drill-down (web).
 *
 * 2026-06-24 (governance-record pass): the modal is the record of a governance
 * event, not a stat dump. It now surfaces the REAL meeting data the backend
 * already stores + the list view now exposes:
 *   • AGENDA & decisions — `meeting.agendaItems[]` (title, discussion, and the
 *     `decision` recorded against each at minute-time). A decided item IS a
 *     "resolution tabled" in this model, so the agenda IS the decision record.
 *   • ATTENDEES — `meeting.attendeeUserIds` resolved to names via the trustee
 *     roster (`useTrustTrustees`); apologies are derived (active roster minus
 *     attendees), and the chair is marked. Honest: we show "{n} of {m} present",
 *     NOT a quorum verdict — the deed's quorum rule isn't stored.
 *
 * Unchanged, honest-by-design sections (no per-meeting association exists in the
 * data model — we do NOT fabricate a link):
 *   • Minutes (MTG-01): the trust's `minutes` DocumentDocs, opened via the live
 *     SAS download; honest "confirm the date" note.
 *   • Resolutions (MTG-02): the trust's resolutions, with the explicit
 *     no-per-meeting-link disclosure.
 */

import { useState } from 'react';
import { displayPersonName } from '../../lib/format/person-name';
import { Link } from '@tanstack/react-router';
import { Modal } from '../ui/modal';
import { Icon } from '../icon';
import {
  useTrustResolutions,
  useTrustDocuments,
  useTrustTrustees,
  useTrustMeetings,
  usePatchMeetingAgenda,
  useCanActOnTrust,
  type TrustMeetingView,
  type ResolutionSummary,
} from '../../lib/api';
import {
  useDocumentDownload,
  openDownloadedDocument,
} from '../../lib/api/documents';
import { useToastStore } from '../../lib/toast-store';
import { mapError } from '../../lib/error-messages';
import { initialsOf } from '../../lib/format/initials';

function statusLabel(status: TrustMeetingView['status']): string {
  switch (status) {
    case 'scheduled':
      return 'Scheduled';
    case 'in-progress':
      return 'In progress';
    case 'finalised':
      return 'Minutes signed';
  }
}

function formatMeetingDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function StatTile({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div
        className="eyebrow ink"
        style={{ marginBottom: 6 }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontFamily: 'var(--serif)', color: 'var(--ink)' }}>
        {value}
      </div>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="eyebrow"
      style={{ marginBottom: 12 }}
    >
      {children}
    </div>
  );
}

export interface MeetingDetailModalProps {
  trustId: string;
  meeting: TrustMeetingView | null;
  open: boolean;
  onClose: () => void;
}

export function MeetingDetailModal({
  trustId,
  meeting: meetingProp,
  open,
  onClose,
}: MeetingDetailModalProps) {
  const pushToast = useToastStore((s) => s.pushToast);
  const download = useDocumentDownload();

  // Lane B · B10: the caller passes a list-row SNAPSHOT; after an agenda
  // patch the invalidated meetings query refetches, so prefer the FRESH row
  // (same id) from the cache — a just-recorded decision renders in place
  // instead of waiting for a close/reopen.
  const meetingsQ = useTrustMeetings({ trustId, enabled: open && !!meetingProp });
  const meeting =
    (meetingProp && meetingsQ.data?.meetings.find((m) => m.id === meetingProp.id)) ??
    meetingProp;

  // Minutes are a `minutes`-typed DocumentDoc on the trust.
  const minutesQ = useTrustDocuments({
    trustId,
    type: 'minutes',
    enabled: open && !!meeting,
  });
  const minutesDocs = minutesQ.data?.documents ?? [];

  // Resolutions for the trust (no per-meeting association field exists yet).
  const resolutionsQ = useTrustResolutions({
    trustId,
    limit: 50,
    enabled: open && !!meeting,
  });
  const resolutions: ResolutionSummary[] = resolutionsQ.data?.resolutions ?? [];

  // Trustee roster — resolves attendee userIds → names + derives apologies.
  const trusteesQ = useTrustTrustees(trustId);

  // Lane B · B10: record per-agenda-item decisions while the meeting is still
  // mutable. LOCK RULE = the server's own status gate, no tighter: the backend
  // only accepts patches on draft/circulating (FE mapping: anything that is
  // not 'finalised'); locked/signed → 409. UI additionally tier-gates via
  // useCanActOnTrust (a beneficiary-tier member is read-only).
  const canAct = useCanActOnTrust(trustId);
  const patchAgenda = usePatchMeetingAgenda();
  const agendaEditable = canAct && meeting !== null && meeting.status !== 'finalised';
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [decisionDraft, setDecisionDraft] = useState('');
  const [decisionError, setDecisionError] = useState<string | null>(null);

  function startEditing(index: number, existing: string | undefined) {
    setEditingIndex(index);
    setDecisionDraft(existing ?? '');
    setDecisionError(null);
  }

  function saveDecision() {
    if (!meeting || editingIndex === null || patchAgenda.isPending) return;
    const decision = decisionDraft.trim();
    if (!decision) {
      setDecisionError('Write the decision as it should appear in the minutes.');
      return;
    }
    patchAgenda.mutate(
      { trustId, meetingId: meeting.id, items: [{ index: editingIndex, decision }] },
      {
        onSuccess: () => {
          setEditingIndex(null);
          setDecisionDraft('');
        },
        onError: (err) => {
          setDecisionError(
            err.isStepUpRequired
              ? 'A fresh security check is required to record a decision. Complete it and try again.'
              // MEETING_STATUS_NOT_MUTABLE is the only 409 that means "this
              // meeting is locked" — MEETING_CAS_RACE (T7, 21 Sep audit wave)
              // is also a 409 on this same endpoint and means a concurrent
              // edit was lost, not that the meeting was finalised. Route by
              // code, not by status, or a CAS race reads as a locked meeting.
              : err.code === 'MEETING_STATUS_NOT_MUTABLE'
                ? 'This meeting has been finalised. Its minutes are locked and decisions can no longer change.'
                : mapError(err, 'save'),
          );
        },
      },
    );
  }

  function handleDownload(documentId: string) {
    download.mutate(
      { documentId },
      {
        onSuccess: (res) =>
          openDownloadedDocument(res, (m) => pushToast({ kind: 'info', message: m })),
        onError: (err) => pushToast({ kind: 'error', message: mapError(err, 'load') }),
      },
    );
  }

  if (!meeting) return null;

  // ── Real meeting record (agenda + attendees) ──────────────────────────────
  const agendaItems = meeting.agendaItems ?? [];
  const attendeeIds = meeting.attendeeUserIds ?? [];
  const roster = (trusteesQ.data?.trustees ?? []).filter((t) => t.status === 'active' && !!t.userId);
  const nameById = new Map(roster.map((t) => [t.userId, t.displayName]));
  const present = attendeeIds.map((uid) => ({
    uid,
    name: nameById.get(uid) ?? 'Trustee',
    isChair: uid === meeting.chairUserId,
  }));
  const apologies = roster.filter((t) => !attendeeIds.includes(t.userId!));

  return (
    <Modal open={open} onClose={onClose} title={meeting.title} size="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 8 }}>
        {/* Header line — status + date + (present count when finalised) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 11.5,
              letterSpacing: '0.01em',
              fontWeight: 600,
              color: 'var(--ink-2)',
              background: 'var(--surface-2)',
              padding: '3px 10px',
              borderRadius: 999,
            }}
          >
            {statusLabel(meeting.status)}
          </span>
          <span style={{ fontSize: 13.5, color: 'var(--ink-3)' }}>
            {formatMeetingDate(meeting.date)}
          </span>
          {present.length > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-2)' }}>
              <Icon name="check" size={13} stroke={2.5} color="var(--positive)" />
              {present.length} of {present.length + apologies.length} trustees present
            </span>
          )}
        </div>

        {/* Record stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
          <StatTile label="Agenda items" value={meeting.agendaCount} />
          <StatTile label="Attendees" value={meeting.attendees} />
          <StatTile label="Resolutions tabled" value={meeting.resolutionsTabled} />
        </div>

        {/* Agenda & decisions — the heart of the record */}
        {agendaItems.length > 0 && (
          <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 18 }}>
            <SectionHeading>Agenda &amp; decisions</SectionHeading>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {agendaItems.map((it, i) => (
                <div
                  key={i}
                  style={{
                    border: '1px solid var(--hairline)',
                    borderRadius: 10,
                    padding: '12px 14px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span className="mono" style={{ fontSize: 12, color: 'var(--ink-4)', flexShrink: 0 }}>{i + 1}.</span>
                    <span style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}>{it.title}</span>
                  </div>
                  {it.discussion && (
                    <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55, marginTop: 6, marginLeft: 22 }}>
                      {it.discussion}
                    </div>
                  )}
                  <div style={{ marginTop: 8, marginLeft: 22 }}>
                    {editingIndex === i ? (
                      /* Lane B · B10: inline decision editor. */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <textarea
                          data-testid={`agenda-decision-input-${i}`}
                          value={decisionDraft}
                          rows={2}
                          maxLength={5000}
                          disabled={patchAgenda.isPending}
                          onChange={(e) => setDecisionDraft(e.target.value)}
                          placeholder="The binding decision, as it should read in the minutes"
                          style={{
                            width: '100%', padding: '9px 11px', borderRadius: 8,
                            border: '1px solid var(--hairline-2)', background: 'var(--bg)',
                            color: 'var(--ink)', fontFamily: 'var(--sans)', fontSize: 13.5,
                            lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box',
                          }}
                        />
                        {decisionError && (
                          <span role="alert" style={{ fontSize: 12.5, color: 'var(--negative)' }}>
                            {decisionError}
                          </span>
                        )}
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            type="button"
                            className="btn-secondary"
                            data-testid={`agenda-decision-save-${i}`}
                            disabled={patchAgenda.isPending}
                            onClick={saveDecision}
                            style={{ fontSize: 12, padding: '5px 12px', height: 'auto' }}
                          >
                            {patchAgenda.isPending ? 'Recording…' : 'Record decision'}
                          </button>
                          <button
                            type="button"
                            disabled={patchAgenda.isPending}
                            onClick={() => { setEditingIndex(null); setDecisionError(null); }}
                            style={{
                              border: 'none', background: 'transparent', padding: 0,
                              color: 'var(--ink-3)', fontSize: 12, textDecoration: 'underline', cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : it.decision ? (
                      <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 7, fontSize: 13, color: 'var(--positive)', fontWeight: 500 }}>
                        <Icon name="check" size={13} stroke={2.5} color="var(--positive)" />
                        <span style={{ color: 'var(--ink)' }}>{it.decision}</span>
                        {agendaEditable && (
                          <button
                            type="button"
                            data-testid={`agenda-decision-amend-${i}`}
                            onClick={() => startEditing(i, it.decision)}
                            style={{
                              border: 'none', background: 'transparent', padding: 0, marginLeft: 4,
                              color: 'var(--ink-3)', fontSize: 12, textDecoration: 'underline', cursor: 'pointer',
                            }}
                          >
                            Amend
                          </button>
                        )}
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span className="eyebrow mute">
                          {meeting.status === 'finalised' ? 'Noted, no resolution' : 'Planned'}
                        </span>
                        {agendaEditable && (
                          <button
                            type="button"
                            data-testid={`agenda-decision-record-${i}`}
                            onClick={() => startEditing(i, undefined)}
                            style={{
                              border: 'none', background: 'transparent', padding: 0,
                              color: 'var(--primary-700)', fontSize: 12.5, textDecoration: 'underline',
                              textUnderlineOffset: 2, cursor: 'pointer', fontFamily: 'var(--sans)',
                            }}
                          >
                            Record decision
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attendees — present (from the record) + apologies (roster minus) */}
        {(present.length > 0 || (meeting.status === 'finalised' && apologies.length > 0)) && (
          <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 18 }}>
            <SectionHeading>Attendees</SectionHeading>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {present.map((a) => (
                <div key={a.uid} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="avatar" style={{ width: 30, height: 30, fontSize: 11 }}>{initialsOf(a.name)}</div>
                  <span style={{ fontSize: 14, color: 'var(--ink)', flex: 1 }}>{displayPersonName(a.name)}</span>
                  {a.isChair && (
                    <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.01em', color: 'var(--accent-700)' }}>Chair</span>
                  )}
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.01em', color: 'var(--positive)' }}>Present</span>
                </div>
              ))}
              {meeting.status === 'finalised' && apologies.map((t) => (
                <div key={t.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: 0.7 }}>
                  <div className="avatar unsigned" style={{ width: 30, height: 30, fontSize: 11 }}>{initialsOf(t.displayName)}</div>
                  <span style={{ fontSize: 14, color: 'var(--ink-2)', flex: 1 }}>{displayPersonName(t.displayName)}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.01em', color: 'var(--ink-4)' }}>Apology</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Minutes */}
        <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 18 }}>
          <SectionHeading>Minutes</SectionHeading>
          {minutesQ.isLoading ? (
            <div style={{ fontSize: 13.5, color: 'var(--ink-3)' }} aria-busy="true">
              Loading minutes…
            </div>
          ) : minutesQ.isError ? (
            <div role="alert" style={{ fontSize: 13.5, color: 'var(--ink-3)' }}>
              {mapError(minutesQ.error, 'load')}
            </div>
          ) : minutesDocs.length === 0 ? (
            <div
              style={{
                fontSize: 13.5,
                color: 'var(--ink-3)',
                background: 'var(--surface-2)',
                borderRadius: 8,
                padding: '12px 14px',
              }}
            >
              {meeting.status === 'finalised'
                ? 'No minutes document is on file for this trust yet. Upload the signed minutes from the documents vault.'
                : 'Minutes are recorded once the meeting is held and finalised.'}
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 10 }}>
                Minutes documents on file for this trust. A per-meeting link
                isn&rsquo;t recorded yet, so confirm the date before opening.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {minutesDocs.map((d) => {
                  const downloadable = d.status === 'verified';
                  const isThis =
                    download.isPending && download.variables?.documentId === d.id;
                  return (
                    <div
                      key={d.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        border: '1px solid var(--hairline)',
                        borderRadius: 8,
                        padding: '10px 12px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            background: 'var(--surface-2)',
                            display: 'grid',
                            placeItems: 'center',
                            color: 'var(--ink-2)',
                            flexShrink: 0,
                          }}
                        >
                          <Icon name="doc" size={13} />
                        </div>
                        <span
                          style={{
                            fontSize: 13.5,
                            color: 'var(--ink)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {d.filename}
                        </span>
                      </div>
                      {downloadable ? (
                        <button
                          type="button"
                          data-testid={`minutes-download-${d.id}`}
                          onClick={() => handleDownload(d.id)}
                          disabled={download.isPending}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--primary-700)',
                            cursor: download.isPending ? 'default' : 'pointer',
                            fontSize: 12.5,
                            textDecoration: 'underline',
                            textUnderlineOffset: 2,
                            fontFamily: 'var(--sans)',
                            padding: 0,
                            flexShrink: 0,
                          }}
                        >
                          {isThis ? 'Opening…' : 'View minutes'}
                        </button>
                      ) : (
                        <span
                          style={{ fontSize: 12.5, color: 'var(--ink-4)', flexShrink: 0 }}
                          title="Available once the document passes the malware scan"
                        >
                          Scanning
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Resolutions (MTG-02 — governance chain) */}
        <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 18 }}>
          <SectionHeading>Resolutions</SectionHeading>
          {resolutionsQ.isLoading ? (
            <div style={{ fontSize: 13.5, color: 'var(--ink-3)' }} aria-busy="true">
              Loading resolutions…
            </div>
          ) : resolutionsQ.isError ? (
            <div role="alert" style={{ fontSize: 13.5, color: 'var(--ink-3)' }}>
              {mapError(resolutionsQ.error, 'load')}
            </div>
          ) : resolutions.length === 0 ? (
            <div
              style={{
                fontSize: 13.5,
                color: 'var(--ink-3)',
                background: 'var(--surface-2)',
                borderRadius: 8,
                padding: '12px 14px',
              }}
            >
              No resolutions recorded for this trust yet.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 10 }}>
                The meeting records {meeting.resolutionsTabled} resolution
                {meeting.resolutionsTabled === 1 ? '' : 's'} tabled. A per-meeting
                resolution link isn&rsquo;t recorded yet. These are all the
                trust&rsquo;s resolutions.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {resolutions.map((r) => (
                  <Link
                    key={r.id}
                    to="/app/trusts/$id/resolutions/$rid"
                    params={{ id: trustId, rid: r.id }}
                    onClick={onClose}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      border: '1px solid var(--hairline)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13.5,
                          color: 'var(--ink)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.title}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                        <span className="mono">{r.ref}</span> · {r.signers_signed}/
                        {r.signers_total} signed
                      </div>
                    </div>
                    <Icon name="arrow-right" size={14} />
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <Modal.Footer>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}
